import { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';

import { ThemeProvider } from '@/context/ThemeContext';
import { router } from '@/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import { initializeAuth, selectIsAuthenticated } from '@/store/slices/authSlice';
import { authService } from '@/services/api/authService';
import ErrorBoundary from '@/components/ErrorBoundary';

/**
 * Main App component
 */
function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize auth state by checking HTTP-only cookie on app mount
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  // Proactive token refresh: Refresh access token before it expires
  // Access token expires in 7 days, so we refresh every 5 days to be safe
  // This ensures users stay logged in without interruption
  useEffect(() => {
    if (isAuthenticated) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Refresh token proactively every 6 days (before 7-day access token expiration)
      // Refresh token lasts 45 days — this keeps the session alive continuously
      const REFRESH_INTERVAL = 6 * 24 * 60 * 60 * 1000; // 6 days in milliseconds

      refreshIntervalRef.current = setInterval(async () => {
        try {
          await authService.refreshToken();
          if (import.meta.env.DEV) {
            console.log('✅ Token refreshed proactively');
          }
        } catch (error) {
          console.error('❌ Proactive token refresh failed:', error);
          // If refresh fails, clear interval - user will need to login again
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
          }
        }
      }, REFRESH_INTERVAL);
    } else {
      // Clear interval if user is not authenticated
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isAuthenticated]);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
